/**
 * Human-in-the-Loop Negotiation Page
 * 
 * Production-ready negotiation with step-by-step human approval.
 * AI suggests actions, humans review and approve before execution.
 * 
 * Features:
 * - Real AI-powered supplier behavior analysis
 * - Step-by-step approval workflow
 * - Email draft generation for each round
 * - Full negotiation history with audit trail
 * - BATNA comparison and risk analysis
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Grid,
  Chip,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Handshake,
  PlayArrow,
  CheckCircle,
  Cancel,
  TrendingUp,
  TrendingDown,
  Psychology,
  Lightbulb,
  Warning,
  Info,
  History,
  AttachMoney,
  Email,
  ExpandMore,
  ThumbUp,
  ThumbDown,
  Edit,
  Refresh,
  ContentCopy,
  ArrowForward,
  ArrowBack,
  CompareArrows,
  Shield,
  Timeline,
  LocalOffer,
  Business,
} from '@mui/icons-material';
import { formatUKDate, formatGBP } from '../../utils/ukFormatting';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ==================== Types ====================

interface StartNegotiationForm {
  supplier_id: string;
  supplier_name: string;
  current_price: string;
  target_price: string;
  volume: string;
  category: string;
  batna: string;
  max_rounds: string;
  our_leverage: string;
  relationship_history: string;
}

interface NegotiationSession {
  session_id: string;
  status: string;
  supplier_name: string;
  current_price: number;
  target_price: number;
  our_current_offer: number | null;
  supplier_counter_offer: number | null;
  round_number: number;
  max_rounds: number;
  total_savings: number | null;
  savings_percentage: number | null;
  message: string;
}

interface AISuggestion {
  session_id: string;
  round_number: number;
  supplier_behavior: string;
  supplier_likely_response: string;
  confidence_score: number;
  recommended_action: string;
  suggested_offer: number;
  reasoning: string;
  alternative_actions: Array<{ action: string; rationale: string }>;
  email_draft: string;
  risk_if_accept: string;
  risk_if_reject: string;
  batna_comparison: string;
  market_context: string;
}

interface RoundResult {
  session_id: string;
  round_number: number;
  our_action: string;
  our_offer: number | null;
  supplier_response: string;
  supplier_counter_offer: number | null;
  supplier_message: string;
  session_status: string;
  next_step: string;
  final_price: number | null;
  total_savings: number | null;
  savings_percentage: number | null;
  supplier_email: string;
}

interface NegotiationHistory {
  session_id: string;
  status: string;
  supplier_name: string;
  supplier_behavior: string;
  initial_price: number;
  target_price: number;
  final_price: number | null;
  rounds_completed: number;
  max_rounds: number;
  batna: string;
  created_at: string;
  rounds: Array<{
    round: number;
    timestamp: string;
    our_action: string;
    our_offer: number;
    supplier_response: string;
    supplier_counter_offer: number;
    supplier_message: string;
  }>;
  savings: {
    target_savings: number;
    actual_savings: number | null;
    savings_percentage: number | null;
  };
}

// ==================== Component ====================

const HITLNegotiationPage: React.FC = () => {
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [session, setSession] = useState<NegotiationSession | null>(null);
  const [suggestion, setSuggestion] = useState<AISuggestion | null>(null);
  const [history, setHistory] = useState<NegotiationHistory | null>(null);
  const [roundResult, setRoundResult] = useState<RoundResult | null>(null);
  
  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'success' | 'error' | 'info' });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Form State
  const [form, setForm] = useState<StartNegotiationForm>({
    supplier_id: '',
    supplier_name: '',
    current_price: '',
    target_price: '',
    volume: '1000',
    category: 'general',
    batna: '',
    max_rounds: '5',
    our_leverage: '',
    relationship_history: '',
  });
  
  // Decision State
  const [customOffer, setCustomOffer] = useState<string>('');
  const [customMessage, setCustomMessage] = useState<string>('');
  const [decisionReasoning, setDecisionReasoning] = useState<string>('');

  // ==================== API Calls ====================

  const startNegotiation = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/v1/negotiation/negotiate/demo/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_id: form.supplier_id || `sup-${Date.now()}`,
          supplier_name: form.supplier_name,
          current_price: parseFloat(form.current_price),
          target_price: parseFloat(form.target_price),
          volume: parseInt(form.volume, 10),
          category: form.category,
          batna: form.batna || undefined,
          max_rounds: parseInt(form.max_rounds, 10),
          our_leverage: form.our_leverage || undefined,
          relationship_history: form.relationship_history || undefined,
        }),
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to start negotiation');
      }
      
      const data: NegotiationSession = await response.json();
      setSession(data);
      setActiveStep(1);
      setSnackbar({ open: true, message: 'Negotiation started! Getting AI suggestion...', severity: 'success' });
      
      // Auto-fetch first suggestion
      getSuggestion(data.session_id);
      
    } catch (err: any) {
      setError(err.message);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const getSuggestion = async (sessionId?: string) => {
    const id = sessionId || session?.session_id;
    if (!id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/v1/negotiation/negotiate/demo/${id}/suggest`);
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to get AI suggestion');
      }
      
      const data: AISuggestion = await response.json();
      setSuggestion(data);
      setCustomOffer(data.suggested_offer.toString());
      setActiveStep(2);
      
    } catch (err: any) {
      setError(err.message);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const executeDecision = async (action: string, offer?: number) => {
    if (!session) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/v1/negotiation/negotiate/demo/${session.session_id}/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          custom_offer: offer ?? (customOffer ? parseFloat(customOffer) : undefined),
          custom_message: customMessage || undefined,
          reasoning: decisionReasoning || undefined,
        }),
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to execute decision');
      }
      
      const data: RoundResult = await response.json();
      setRoundResult(data);
      
      // Update session status
      setSession(prev => prev ? {
        ...prev,
        status: data.session_status,
        round_number: data.round_number,
        our_current_offer: data.our_offer,
        supplier_counter_offer: data.supplier_counter_offer,
        total_savings: data.total_savings,
        savings_percentage: data.savings_percentage,
      } : null);
      
      if (data.session_status === 'completed' || data.session_status === 'failed') {
        setActiveStep(4); // Final step
        setSnackbar({ 
          open: true, 
          message: data.session_status === 'completed' 
            ? `Negotiation complete! Final price: $${data.final_price?.toFixed(2)}`
            : 'Negotiation ended without agreement',
          severity: data.session_status === 'completed' ? 'success' : 'info' 
        });
      } else {
        setActiveStep(3); // Show result, then can get next suggestion
        // Reset form for next round
        setCustomMessage('');
        setDecisionReasoning('');
      }
      
    } catch (err: any) {
      setError(err.message);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (!session) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/v1/negotiation/negotiate/demo/${session.session_id}/history`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch history');
      }
      
      const data: NegotiationHistory = await response.json();
      setHistory(data);
      setHistoryDialogOpen(true);
      
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard!', severity: 'info' });
  };

  const resetNegotiation = () => {
    setSession(null);
    setSuggestion(null);
    setHistory(null);
    setRoundResult(null);
    setActiveStep(0);
    setForm({
      supplier_id: '',
      supplier_name: '',
      current_price: '',
      target_price: '',
      volume: '1000',
      category: 'general',
      batna: '',
      max_rounds: '5',
      our_leverage: '',
      relationship_history: '',
    });
    setCustomOffer('');
    setCustomMessage('');
    setDecisionReasoning('');
  };

  // ==================== Render Helpers ====================

  const renderSetupStep = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business /> Negotiation Setup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure the negotiation parameters. AI will analyze the supplier and suggest optimal strategies.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Supplier Name"
              value={form.supplier_name}
              onChange={(e) => setForm(prev => ({ ...prev, supplier_name: e.target.value }))}
              placeholder="e.g., TechParts International"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={form.category}
                label="Category"
                onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
              >
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="electronics">Electronics</MenuItem>
                <MenuItem value="raw_materials">Raw Materials</MenuItem>
                <MenuItem value="packaging">Packaging</MenuItem>
                <MenuItem value="logistics">Logistics</MenuItem>
                <MenuItem value="services">Services</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              type="number"
              label="Current Price ($)"
              value={form.current_price}
              onChange={(e) => setForm(prev => ({ ...prev, current_price: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              type="number"
              label="Target Price ($)"
              value={form.target_price}
              onChange={(e) => setForm(prev => ({ ...prev, target_price: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Order Volume"
              value={form.volume}
              onChange={(e) => setForm(prev => ({ ...prev, volume: e.target.value }))}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="BATNA (Best Alternative)"
              value={form.batna}
              onChange={(e) => setForm(prev => ({ ...prev, batna: e.target.value }))}
              placeholder="e.g., Alternative supplier OmniTech can offer $105 per unit"
              helperText="Your best alternative if this negotiation fails"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Our Leverage"
              value={form.our_leverage}
              onChange={(e) => setForm(prev => ({ ...prev, our_leverage: e.target.value }))}
              placeholder="e.g., We're their largest customer (25% of sales)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Relationship History"
              value={form.relationship_history}
              onChange={(e) => setForm(prev => ({ ...prev, relationship_history: e.target.value }))}
              placeholder="e.g., 3-year partnership, generally reliable"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Max Rounds"
              value={form.max_rounds}
              onChange={(e) => setForm(prev => ({ ...prev, max_rounds: e.target.value }))}
              inputProps={{ min: 1, max: 10 }}
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
            onClick={startNegotiation}
            disabled={isLoading || !form.supplier_name || !form.current_price || !form.target_price}
          >
            Start Negotiation
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderAISuggestion = () => {
    if (!suggestion) return null;
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Psychology color="primary" /> AI Recommendation - Round {suggestion.round_number}
            </Typography>
            <Chip 
              label={`${(suggestion.confidence_score * 100).toFixed(0)}% Confidence`}
              color={suggestion.confidence_score > 0.7 ? 'success' : suggestion.confidence_score > 0.5 ? 'warning' : 'error'}
              size="small"
            />
          </Box>
          
          <Grid container spacing={3}>
            {/* Main Recommendation */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  RECOMMENDED ACTION
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Chip 
                    label={suggestion.recommended_action.toUpperCase()} 
                    color="primary" 
                    size="medium"
                    sx={{ fontWeight: 'bold' }}
                  />
                  {suggestion.recommended_action === 'counter' && (
                    <Typography variant="h5" color="primary" fontWeight="bold">
                      ${suggestion.suggested_offer.toFixed(2)}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2">{suggestion.reasoning}</Typography>
              </Paper>
              
              {/* Supplier Analysis */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Supplier Behavior Analysis</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Chip 
                      icon={<CompareArrows />} 
                      label={`Type: ${suggestion.supplier_behavior}`} 
                      variant="outlined" 
                      size="small" 
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Chip 
                      icon={<TrendingUp />} 
                      label={suggestion.supplier_likely_response} 
                      variant="outlined" 
                      size="small" 
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            
            {/* Risk Analysis */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Shield fontSize="small" /> Risk Analysis
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">If Accept:</Typography>
                  <Typography variant="body2" color="error.main">{suggestion.risk_if_accept}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">If Reject:</Typography>
                  <Typography variant="body2" color="warning.main">{suggestion.risk_if_reject}</Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">BATNA Comparison:</Typography>
                <Typography variant="body2">{suggestion.batna_comparison}</Typography>
              </Paper>
            </Grid>
            
            {/* Alternative Actions */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Alternative Actions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {suggestion.alternative_actions.map((alt, i) => (
                      <Grid item xs={12} md={6} key={i}>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Chip label={alt.action.toUpperCase()} size="small" sx={{ mb: 1 }} />
                          <Typography variant="body2">{alt.rationale}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            
            {/* Email Draft */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" /> Email Draft
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {suggestion.email_draft}
                  </Paper>
                  <Button
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={() => copyToClipboard(suggestion.email_draft)}
                    sx={{ mt: 1 }}
                  >
                    Copy Email
                  </Button>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
          
          {/* Decision Section */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Your Decision</Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Your Offer ($)"
                value={customOffer}
                onChange={(e) => setCustomOffer(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                helperText={`AI suggested: $${suggestion.suggested_offer.toFixed(2)}`}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Custom Message (optional)"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Any additional message to include"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Your Reasoning (for audit trail)"
                value={decisionReasoning}
                onChange={(e) => setDecisionReasoning(e.target.value)}
                placeholder="Why are you making this decision?"
              />
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ThumbUp />}
              onClick={() => executeDecision('counter', parseFloat(customOffer))}
              disabled={isLoading || !customOffer}
            >
              Counter at ${customOffer || '0'}
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => executeDecision('accept')}
              disabled={isLoading}
            >
              Accept Current Offer
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ArrowBack />}
              onClick={() => executeDecision('walk_away')}
              disabled={isLoading}
            >
              Walk Away (Use BATNA)
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => executeDecision('reject')}
              disabled={isLoading}
            >
              Reject Outright
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderRoundResult = () => {
    if (!roundResult) return null;
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Handshake /> Round {roundResult.round_number} Result
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" gutterBottom>Our Action</Typography>
                <Chip label={roundResult.our_action.toUpperCase()} color="primary" />
                {roundResult.our_offer && (
                  <Typography variant="h5" sx={{ mt: 1 }}>${roundResult.our_offer.toFixed(2)}</Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: roundResult.supplier_response === 'accepted' ? 'success.50' : 'warning.50' }}>
                <Typography variant="subtitle2" gutterBottom>Supplier Response</Typography>
                <Chip 
                  label={roundResult.supplier_response.toUpperCase()} 
                  color={roundResult.supplier_response === 'accepted' ? 'success' : 
                         roundResult.supplier_response === 'rejected' ? 'error' : 'warning'}
                />
                {roundResult.supplier_counter_offer && (
                  <Typography variant="h5" sx={{ mt: 1 }}>${roundResult.supplier_counter_offer.toFixed(2)}</Typography>
                )}
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info" icon={<Info />}>
                <AlertTitle>Supplier Message</AlertTitle>
                {roundResult.supplier_message}
              </Alert>
            </Grid>
            
            {roundResult.session_status === 'completed' && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: 'success.50', textAlign: 'center' }}>
                  <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>Negotiation Complete!</Typography>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    Final Price: ${roundResult.final_price?.toFixed(2)}
                  </Typography>
                  {roundResult.total_savings && (
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Total Savings: ${roundResult.total_savings.toFixed(2)} ({roundResult.savings_percentage?.toFixed(1)}%)
                    </Typography>
                  )}
                </Paper>
              </Grid>
            )}
            
            {roundResult.session_status === 'awaiting_human' && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<ArrowForward />}
                    onClick={() => getSuggestion()}
                  >
                    Get Next Round Suggestion
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<History />}
                    onClick={fetchHistory}
                  >
                    View History
                  </Button>
                </Box>
              </Grid>
            )}
            
            {/* Supplier Email */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" /> Supplier's Email
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {roundResult.supplier_email}
                  </Paper>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderHistoryDialog = () => (
    <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Timeline /> Negotiation History
      </DialogTitle>
      <DialogContent>
        {history && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Supplier</Typography>
                <Typography variant="body1" fontWeight="bold">{history.supplier_name}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Behavior Type</Typography>
                <Chip label={history.supplier_behavior} size="small" />
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Initial → Target</Typography>
                <Typography variant="body1">${history.initial_price} → ${history.target_price}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Chip 
                  label={history.status} 
                  color={history.status === 'completed' ? 'success' : 'warning'}
                  size="small"
                />
              </Grid>
            </Grid>
            
            <Typography variant="subtitle2" gutterBottom>Rounds</Typography>
            <List>
              {history.rounds.map((round, i) => (
                <ListItem key={i} sx={{ bgcolor: 'grey.50', mb: 1, borderRadius: 1 }}>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>{round.round}</Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Chip label={round.our_action} size="small" color="primary" />
                        <Typography>Our offer: ${round.our_offer?.toFixed(2) || 'N/A'}</Typography>
                        <ArrowForward fontSize="small" />
                        <Chip label={round.supplier_response} size="small" color="warning" />
                        <Typography>Counter: ${round.supplier_counter_offer?.toFixed(2) || 'N/A'}</Typography>
                      </Box>
                    }
                    secondary={round.supplier_message}
                  />
                </ListItem>
              ))}
            </List>
            
            {history.savings && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'success.50' }}>
                <Typography variant="subtitle2">Savings Summary</Typography>
                <Typography variant="body2">Target: ${history.savings.target_savings.toFixed(2)}</Typography>
                {history.savings.actual_savings && (
                  <Typography variant="body2" color="success.main" fontWeight="bold">
                    Actual: ${history.savings.actual_savings.toFixed(2)} ({history.savings.savings_percentage?.toFixed(1)}%)
                  </Typography>
                )}
              </Paper>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // ==================== Main Render ====================

  const steps = [
    { label: 'Setup', description: 'Configure negotiation parameters' },
    { label: 'Session Started', description: 'AI analyzing supplier' },
    { label: 'Review AI Suggestion', description: 'Approve or modify recommendation' },
    { label: 'Round Complete', description: 'Review supplier response' },
    { label: 'Negotiation Complete', description: 'Final outcome' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Handshake /> HITL Negotiation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI-powered negotiation with human oversight and approval
          </Typography>
        </Box>
        {session && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<History />} onClick={fetchHistory}>
              History
            </Button>
            <Button variant="outlined" color="error" startIcon={<Refresh />} onClick={resetNegotiation}>
              New Negotiation
            </Button>
          </Box>
        )}
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Progress Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>
      
      {/* Session Info */}
      {session && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Session</Typography>
              <Typography variant="body2" fontFamily="monospace">{session.session_id}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Supplier</Typography>
              <Typography variant="body2" fontWeight="bold">{session.supplier_name}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Current Price</Typography>
              <Typography variant="body2">${session.current_price.toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Target Price</Typography>
              <Typography variant="body2" color="success.main" fontWeight="bold">${session.target_price.toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Round</Typography>
              <Typography variant="body2">{session.round_number} / {session.max_rounds}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Chip 
                label={session.status} 
                size="small" 
                color={session.status === 'completed' ? 'success' : 
                       session.status === 'failed' ? 'error' : 'warning'}
              />
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {/* Main Content */}
      {activeStep === 0 && renderSetupStep()}
      {activeStep >= 2 && suggestion && renderAISuggestion()}
      {activeStep >= 3 && roundResult && renderRoundResult()}
      
      {/* Loading Overlay */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* History Dialog */}
      {renderHistoryDialog()}
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
};

export default HITLNegotiationPage;
